import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { getCSRFToken } from '@/lib/utils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      withCredentials: true, // CRITICAL: Include cookies for session-based auth
      headers: {
        'Content-Type': 'application/json',
      },
      // Send array params as repeated keys rather than indexed brackets.
      paramsSerializer: { indexes: null },
    });

    // Request interceptor to add CSRF token for mutating requests
    this.client.interceptors.request.use(
      (config) => {
        // Add CSRF token for non-GET requests
        if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
          const csrfToken = getCSRFToken();
          if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        // AWS WAF answers a request it wants to challenge with a 202 interstitial page. Surface it
        // as an error so it isn't taken for the API's own reply.
        if (response.headers?.['x-amzn-waf-action']) {
          return Promise.reject(
            new AxiosError(
              'Request challenged by AWS WAF',
              AxiosError.ERR_BAD_RESPONSE,
              response.config,
              response.request,
              response
            )
          );
        }
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          console.warn('Unauthorized request:', error.config?.url);
          // Auth check will handle redirect in useAuthStore
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();
